import { Empty, Form, FormItemProps, Select, SelectProps, Spin } from 'antd'
import './style.less'
import { useEffect, useState, useMemo } from 'react'
import { debounce, isEqual, uniqWith } from 'lodash'

export type FORM_ITEM_INFINITE_SELECT_TYPE = {
  formItemProps: FormItemProps
  selectProps?: SelectProps
  isActive?: boolean
  fetchData: (params: {
    page_number: number
    page_size: number
    search?: string
    name?: string
  }) => Promise<any>
  renderOption: (item: any) => React.ReactNode
  getOptionKey: (item: any) => string
  getOptionValue: (item: any) => string
  searchParamName?: 'search' | 'name' // Different APIs use different param names
}

export default function FormItemInfiniteSelect(
  props: FORM_ITEM_INFINITE_SELECT_TYPE
) {
  const {
    formItemProps,
    selectProps,
    isActive = false,
    fetchData,
    renderOption,
    getOptionKey,
    getOptionValue,
    searchParamName = 'search',
  } = props

  const [pagination, setPagination] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [loadMore, setLoadMore] = useState(false)
  const [paginationMeta, setPaginationMeta] = useState<any>({})
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  const maxHeight = 450

  // Reset data when search changes
  useEffect(() => {
    setData([])
    setPagination(1)
  }, [searchTerm])

  // Fetch data when pagination, isActive, or searchTerm changes
  useEffect(() => {
    if (!isActive) return

    setLoading(true)
    handleFetchData(() => {
      setLoading(false)
    })
  }, [pagination, isActive, searchTerm])

  const handleFetchData = async (cb) => {
    const params: any = {
      page_number: pagination,
      page_size: 15,
    }

    // Add search parameter with the correct param name
    if (searchTerm) {
      params[searchParamName] = searchTerm
    }

    try {
      const response = await fetchData(params)
      const responseData = response?.data || []
      const responsePagination = response?.pagination || {}

      setPaginationMeta(responsePagination)
      setLoadMore(false)

      // Append new data to existing data
      setData((prevData) => {
        const result = [...prevData, ...responseData]
        return uniqWith(result, isEqual)
      })

      // Always clear loading state, regardless of pagination format
      cb()
    } catch (error) {
      console.error('Error fetching data:', error)
      setLoadMore(false)
      cb()
    }
  }

  const handleScroll = (event) => {
    event.persist()
    const currentTarget = event.currentTarget
    const offsetHeight = currentTarget.scrollTop + currentTarget.offsetHeight
    const scrollHeight = currentTarget.scrollHeight - 500 // Trigger 500px before bottom

    if (offsetHeight >= scrollHeight) {
      const lastPage = Number(
        paginationMeta?.last_page ?? paginationMeta?.total_page ?? pagination
      )
      if (!loadMore && pagination < lastPage) {
        setLoadMore(true)
        setPagination((prev) => {
          return prev < lastPage ? prev + 1 : prev
        })
      }
    }
  }

  // Create stable debounced function that persists across renders
  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 800),
    []
  )

  // Clean up debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectProps}
        allowClear
        showSearch
        showArrow
        size="large"
        mode="multiple"
        filterOption={false}
        loading={loading}
        onSearch={debouncedSearch}
        listHeight={maxHeight}
        virtual={false}
        onPopupScroll={handleScroll}
        optionFilterProp="children"
        onBlur={() => {
          setSearchTerm('')
        }}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )
        }
      >
        {data?.map((item) => (
          <Select.Option key={getOptionKey(item)} value={getOptionValue(item)}>
            {renderOption(item)}
          </Select.Option>
        ))}
        {loading && (
          <div style={{ backgroundColor: 'white' }}>
            <Spin size="small" />
          </div>
        )}
      </Select>
    </Form.Item>
  )
}
